import * as React from 'react';
import { cn } from '../../utils/cn';

type SpacingValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;

export interface BoxProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  as?: React.ElementType;

  // Padding
  p?: SpacingValue;
  px?: SpacingValue;
  py?: SpacingValue;
  pt?: SpacingValue;
  pr?: SpacingValue;
  pb?: SpacingValue;
  pl?: SpacingValue;

  // Margin
  m?: SpacingValue;
  mx?: SpacingValue;
  my?: SpacingValue;
  mt?: SpacingValue;
  mr?: SpacingValue;
  mb?: SpacingValue;
  ml?: SpacingValue;

  // Border
  border?: boolean;
  borderColor?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

  // Background
  bg?: string;

  // Layout
  display?: 'block' | 'inline-block' | 'inline' | 'flex' | 'inline-flex' | 'grid' | 'hidden';
  position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';

  // Dimensions
  w?: 'auto' | 'full' | '1/2' | '1/3' | '2/3' | '1/4' | '3/4' | string;
  h?: 'auto' | 'full' | 'screen' | string;
}

const getSpacingClass = (prefix: string, value: SpacingValue): string => {
  return `${prefix}-${value}`;
};

const getRoundedClass = (value: BoxProps['rounded']): string => {
  const map = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full',
  };
  return value ? map[value] : '';
};

const getDisplayClass = (value: BoxProps['display']): string => {
  return value || '';
};

const getPositionClass = (value: BoxProps['position']): string => {
  return value || '';
};

const getWidthClass = (value: BoxProps['w']): string => {
  if (!value) return '';
  const map: Record<string, string> = {
    auto: 'w-auto',
    full: 'w-full',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '2/3': 'w-2/3',
    '1/4': 'w-1/4',
    '3/4': 'w-3/4',
  };
  return map[value] || (value.startsWith('w-') ? value : `w-[${value}]`);
};

const getHeightClass = (value: BoxProps['h']): string => {
  if (!value) return '';
  const map: Record<string, string> = {
    auto: 'h-auto',
    full: 'h-full',
    screen: 'h-screen',
  };
  return map[value] || (value.startsWith('h-') ? value : `h-[${value}]`);
};

export const Box = React.forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      as: Component = 'div',
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      border,
      borderColor,
      rounded,
      bg,
      display,
      position,
      w,
      h,
      className,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      // Padding
      p !== undefined && getSpacingClass('p', p),
      px !== undefined && getSpacingClass('px', px),
      py !== undefined && getSpacingClass('py', py),
      pt !== undefined && getSpacingClass('pt', pt),
      pr !== undefined && getSpacingClass('pr', pr),
      pb !== undefined && getSpacingClass('pb', pb),
      pl !== undefined && getSpacingClass('pl', pl),

      // Margin
      m !== undefined && getSpacingClass('m', m),
      mx !== undefined && getSpacingClass('mx', mx),
      my !== undefined && getSpacingClass('my', my),
      mt !== undefined && getSpacingClass('mt', mt),
      mr !== undefined && getSpacingClass('mr', mr),
      mb !== undefined && getSpacingClass('mb', mb),
      ml !== undefined && getSpacingClass('ml', ml),

      // Border
      border && 'border',
      borderColor && `border-${borderColor}`,
      getRoundedClass(rounded),

      // Background
      bg && `bg-${bg}`,

      // Layout
      getDisplayClass(display),
      getPositionClass(position),

      // Dimensions
      getWidthClass(w),
      getHeightClass(h),

      className
    );

    return (
      <Component ref={ref} className={classes} {...props}>
        {children}
      </Component>
    );
  }
);

Box.displayName = 'Box';
